/* eslint-disable react/prop-types */
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import classNames from "classnames";
import React, { useState, useContext, useEffect } from "react";
import AppContext from "context/Context";
import { Button, Form } from "react-bootstrap";
import Flex from "../Flex";
import { useTranslation } from "react-i18next";
import UpliftCss from "../upliftcss.module.css";

export const AdvanceTableFooter = ({
	page,
	pageSize,
	pageIndex,
	rowCount,
	setPageSize,
	canPreviousPage,
	canNextPage,
	viewAllBtn,
	nextPage,
	previousPage,
	rowInfo,
	perPage,
	rowsPerPageSelection,
	navButtons,
	rowsPerPageOptions = [5, 10, 15],
	className,
	jumpToPage,
}) => {
	const [t, i18n] = useTranslation();
	const {
		config: { isRTL },
	} = useContext(AppContext);
	const [isAllVisible, setIsAllVisible] = useState(false);
	const [isMobile, setIsMobile] = useState(false);

	// Check if we're on mobile
	useEffect(() => {
		const checkIfMobile = () => {
			setIsMobile(window.innerWidth < 768);
		};
		
		checkIfMobile();
		
		// Add event listener for window resize
		window.addEventListener('resize', checkIfMobile);
		
		return () => window.removeEventListener('resize', checkIfMobile);
	}, []);

	// Render different layouts based on screen size
	if (isMobile) {
		// Mobile layout
		return (
			<div className={classNames(className, UpliftCss.mobileFooterContainer)}>
				{rowInfo && (
					<div className={UpliftCss.mobileInfoSection}>
						<p className="mb-0">
							{pageSize * pageIndex + 1} {t("to")} {pageSize * pageIndex + page.length} {t("of")} {rowCount}
							
							{viewAllBtn && (
								<>
									<span className="ms-2">&mdash;</span>
									<Button
										variant="link"
										size="sm"
										className="py-2 px-0 fw-semi-bold ms-2"
										onClick={() => {
											setIsAllVisible(!isAllVisible);
											setPageSize(isAllVisible ? perPage : rowCount);
										}}
									>
										View {isAllVisible ? "less" : "all"}
										<FontAwesomeIcon icon="chevron-right" className="ms-1 fs--2" />
									</Button>
								</>
							)}
						</p>
					</div>
				)}
				
				{rowsPerPageSelection && (
					<div className={UpliftCss.mobileRowsPerPage}>
						<p className="mb-0 me-2">{t("Rows per page:")}</p>
						<Form.Select size="sm" className="w-auto" onChange={(e) => setPageSize(Number(e.target.value))} defaultValue={pageSize}>
							{rowsPerPageOptions.map((value) => (
								<option value={value} key={value}>
									{value}
								</option>
							))}
						</Form.Select>
					</div>
				)}
				
				{navButtons && (
					<div className={UpliftCss.mobileNavButtons}>
						<Button 
							size="sm" 
							variant={canPreviousPage ? "primary" : "light"} 
							onClick={() => previousPage()} 
							className={classNames(UpliftCss.mobileNavButton, { disabled: !canPreviousPage })}
						>
							<FontAwesomeIcon icon={isRTL ? "chevron-right" : "chevron-left"} className="me-1 fs--2" />
							<span className={UpliftCss.mobileButtonText}>{t("Previous")}</span>
						</Button>
						
						{rowCount > 0 && jumpToPage && (
							<Form.Select 
								size="sm" 
								className={UpliftCss.mobilePageSelector}
								value={pageIndex}
								onChange={(e) => {
									const page = Number(e.target.value) + 1;
									jumpToPage(page);
								}}
							>
								{Array.from({ length: Math.ceil(rowCount / pageSize) }, (_, i) => (
									<option key={i} value={i}>
										{isRTL 
											? `${Math.ceil(rowCount / pageSize)}/${i + 1}` 
											: `${i + 1}/${Math.ceil(rowCount / pageSize)}`
										}
									</option>
								))}
							</Form.Select>
						)}
						
						<Button
							size="sm"
							variant={canNextPage ? "primary" : "light"}
							className={classNames(UpliftCss.mobileNavButton, {
								disabled: !canNextPage,
							})}
							onClick={() => nextPage()}
						>
							<span className={UpliftCss.mobileButtonText}>{t("Next")}</span>
							<FontAwesomeIcon icon={isRTL ? "chevron-left" : "chevron-right"} className="ms-1 fs--2" />
						</Button>
					</div>
				)}
			</div>
		);
	}
	
	// Desktop layout
	return (
		<Flex className={classNames(className, "align-items-center justify-content-between")}>
			<Flex alignItems="center" className="fs--1">
				{rowInfo && (
					<p className="mb-0">
						<span className="d-none d-sm-inline-block me-2">
							{pageSize * pageIndex + 1} {t("to")} {pageSize * pageIndex + page.length} {t("of")} {rowCount}
						</span>
						{viewAllBtn && (
							<>
								<span className="d-none d-sm-inline-block me-2">&mdash;</span>
								<Button
									variant="link"
									size="sm"
									className="py-2 px-0 fw-semi-bold"
									onClick={() => {
										setIsAllVisible(!isAllVisible);
										setPageSize(isAllVisible ? perPage : rowCount);
									}}
								>
									View {isAllVisible ? "less" : "all"}
									<FontAwesomeIcon icon="chevron-right" className="ms-1 fs--2" />
								</Button>
							</>
						)}
					</p>
				)}
				{rowsPerPageSelection && (
					<>
						<p className="mb-0 mx-2">{t("Rows per page:")}</p>
						<Form.Select size="sm" className="w-auto" onChange={(e) => setPageSize(Number(e.target.value))} defaultValue={pageSize}>
							{rowsPerPageOptions.map((value) => (
								<option value={value} key={value}>
									{value}
								</option>
							))}
						</Form.Select>
					</>
				)}
			</Flex>
			{navButtons && (
				<Flex>
					<Button size="sm" variant={canPreviousPage ? "primary" : "light"} onClick={() => previousPage()} className={classNames("mx-2", { disabled: !canPreviousPage })}>
						<FontAwesomeIcon icon={isRTL ? "chevron-right" : "chevron-left"} className="ms-1 fs--2" />
						{t("Previous")}
					</Button>
					
					{rowCount > 0 && jumpToPage && (
						<Form.Select 
							size="sm" 
							className="mx-2" 
							style={{ width: "auto" }}
							value={pageIndex}
							onChange={(e) => {
								const page = Number(e.target.value) + 1;
								jumpToPage(page);
							}}
						>
							{Array.from({ length: Math.ceil(rowCount / pageSize) }, (_, i) => (
								<option key={i} value={i}>
									{t("Page")} {i + 1} {t("of")} {Math.ceil(rowCount / pageSize)}
								</option>
							))}
						</Form.Select>
					)}
					
					<Button
						size="sm"
						variant={canNextPage ? "primary" : "light"}
						className={classNames("px-4 ms-2", {
							disabled: !canNextPage,
						})}
						onClick={() => nextPage()}
					>
						{t("Next")}
						<FontAwesomeIcon icon={isRTL ? "chevron-left" : "chevron-right"} className="ms-1 fs--2" />
					</Button>
				</Flex>
			)}
		</Flex>
	);
};

export default AdvanceTableFooter;
